import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Toaster, toast } from "react-hot-toast";
import Select, { MultiValue, ActionMeta } from "react-select";
import {
  fetchAllowedMenus,
  fetchUserGroupData,
  fetchUserData,
  updateUser,
  fetchAllUserType,
  userTypeItem,
  UpdateUserTargets,
  fetchAllAssignmentTypes,
  fetchAllAssigedProject,
  fetchAllCampaignServices,
  fetchProjectData,
  fetchMeadiaSourceData,
  fetchAllSubMediaSource,
} from "../Services/APIService";
import { on } from "events";

interface ContactFormData {
  email: string;
  firstName: string;
  lastName: string;
  contactOwner: string;
  jobTitle: string;
  phoneNumber: string;
}

interface User {
  tenantId: number;
  userId: number;
  userName: string;
  mobile: string;
  email: string;
  alternativePhone: string;
  reportTo: number;
  groupId: number;
  isFallbackUser: boolean;
  isActive: boolean;
  userStatusId: number;
  userTypeId: number;
  reportToUserName: string;
  groupName: string;
}

interface CreateContactModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: ContactFormData) => void;
  selectedRows?: User[];
  editUserData?: User | null;
  onUserDataUpdated?: () => void;
}

interface MenuItem {
  menuID: number;
  menuName: string;
  menuUrl: string;
  icon: string;
  subMenuList?: [];
}

interface UserGroup {
  groupId: number;
  groupName: string;
}

interface UserData {
  userId: number;
  userName: string;
  email: string;
}

interface SelectOption {
  value: number;
  label: string;
  userData: UserData;
}

interface ValidationErrors {
  userName?: string;
  mobile?: string;
  email?: string;
  alternativePhone?: string;
  password?: string;
  groupId?: string;
  userTypeId?: string;
}

interface GenericAssignmentOption {
  value: number;
  label: string;
}

interface GenericAssignmentItem {
  id: number;
  name: string;
  isActive?: boolean;
}

interface DynamicAssignmentState {
  options: GenericAssignmentOption[];
  selected: GenericAssignmentOption[];
  previousSelected: number[];
  isDropdownChanged: boolean;
  isLoading: boolean;
  typeName: string;
}

interface UserAssignmentRequest {
  userId: number;
  assignmentTypeId: number;
  listOfAssignments: { assignmentObjectId: number }[];
  isAssigned: boolean;
  tenantId: number;
  createdBy: number;
}

const EditUserViewDeom: React.FC<CreateContactModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  selectedRows = [],
  editUserData = null,
  onUserDataUpdated, // Default to no-op if not provided
}) => {
  const [activeTab, setActiveTab] = useState("tab1");
  const [isSubmittingAssignment, setIsSubmittingAssignment] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [allowedMenus, setAllowedMenus] = useState<MenuItem[]>([]);
  const [userGroups, setUserGroups] = useState<UserGroup[]>([]);
  const [users, setUsers] = useState<UserData[]>([]);
  const [userOptions, setUserOptions] = useState<SelectOption[]>([]);
  const [userTypes, setUserTypes] = useState<userTypeItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingUserTypes, setIsLoadingUserTypes] = useState(true);
  const [errors, setErrors] = useState<ValidationErrors>({});
  const navigate = useNavigate();
  const location = useLocation();
  const [isModalShow, setisModalShow] = useState(true);
  const [userData, setUserData] = useState<User | null>(null);
  const [dynamicAssignmentStates, setDynamicAssignmentStates] = useState<
    Record<number, DynamicAssignmentState>
  >({});
  const [dynamicAssignmentTypeIds, setDynamicAssignmentTypeIds] = useState<
    number[]
  >([]);

  const [formData, setFormData] = useState({
    userId: "",
    userName: "",
    mobile: "",
    email: "",
    alternativePhone: "",
    reportTo: null as UserData | null,
    password: "",
    groupId: "",
    isFallbackUser: false,
    isActive: true,
    userTypeId: "",
  });

  const toggleSidebar = () => {
    setIsSidebarCollapsed(!isSidebarCollapsed);
  };

  const populateFormWithEditData = (userData: User) => {
    const reportToUser = users.find(
      (user) => user.userId === userData.reportTo
    );

    setFormData({
      userId: userData.userId.toString(),
      userName: userData.userName,
      mobile: userData.mobile,
      email: userData.email,
      alternativePhone: userData.alternativePhone || "",
      reportTo: reportToUser || null,
      password: "",
      groupId: userData.groupId.toString(),
      isFallbackUser: userData.isFallbackUser,
      isActive: userData.isActive,
      userTypeId: userData.userTypeId.toString(),
    });
  };

  // Set userData for edit mode
  useEffect(() => {
    if (editUserData) {
      setUserData(editUserData);
    }
  }, [editUserData]);

  // Initialize assignment types
  useEffect(() => {
    const initializeAssignmentTypes = async () => {
      try {
        const allAssignmentTypes = await fetchAllAssignmentTypes();
        const filteredTypes = allAssignmentTypes;
        const typeIds = filteredTypes.map((type) => type.typeId);
        setDynamicAssignmentTypeIds(typeIds);

        const initialStates: Record<number, DynamicAssignmentState> = {};
        filteredTypes.forEach((type) => {
          initialStates[type.typeId] = {
            options: [],
            selected: [],
            previousSelected: [],
            isDropdownChanged: false,
            isLoading: true,
            typeName: type.typeName,
          };
        });

        setDynamicAssignmentStates(initialStates);
      } catch (error) {
        console.error("Error initializing assignment types:", error);
        toast.error("Failed to load assignment types");
      }
    };

    initializeAssignmentTypes();
  }, []);

  // Load assignment data
  useEffect(() => {
    const loadAssignmentData = async () => {
      if (dynamicAssignmentTypeIds.length === 0) return;

      try {
        const fetchPromises = dynamicAssignmentTypeIds.map(async (typeId) => {
          const items = await fetchAssignmentDataByType(typeId);
          const options = items.map((item) => ({
            value: item.id,
            label: item.name,
          }));
          return { typeId, options };
        });

        const results = await Promise.all(fetchPromises);

        setDynamicAssignmentStates((prevStates) => {
          const updatedStates = { ...prevStates };
          results.forEach(({ typeId, options }) => {
            updatedStates[typeId] = {
              ...prevStates[typeId],
              options,
              isLoading: false,
            };
          });
          return updatedStates;
        });
      } catch (error) {
        console.error("Error loading assignment data:", error);
        toast.error("Failed to load assignment data");
      }
    };

    loadAssignmentData();
  }, [dynamicAssignmentTypeIds]);

  // Load user assignments
  useEffect(() => {
    const loadUserAssignments = async () => {
      if (!userData?.userId || dynamicAssignmentTypeIds.length === 0) return;

      try {
        for (const typeId of dynamicAssignmentTypeIds) {
          const assignedItems = await fetchAllAssigedProject(
            userData.userId,
            typeId
          );

          const selected = assignedItems?.length
            ? assignedItems.map((item) => ({
                value: item.assignmentObjectId,
                label: item.assignmentName,
              }))
            : [];

          const previousSelected = selected.map((item) => item.value);

          setDynamicAssignmentStates((prevStates) => ({
            ...prevStates,
            [typeId]: {
              ...prevStates[typeId],
              selected,
              previousSelected,
              isDropdownChanged: false,
              isLoading: false,
            },
          }));
        }
      } catch (error) {
        console.error("Error loading user assignments:", error);
        toast.error("Failed to load user assignments");
      }
    };

    loadUserAssignments();
  }, [userData?.userId, dynamicAssignmentTypeIds]);

  // Fetch assignment data by type
  const fetchAssignmentDataByType = async (
    typeId: number
  ): Promise<GenericAssignmentItem[]> => {
    try {
      let result: GenericAssignmentItem[] = [];
      switch (typeId) {
        case 1: {
          const campaignServices = await fetchAllCampaignServices();
          result = campaignServices.map((service) => ({
            id: service.campaignId,
            name: service.campaignName,
          }));
          break;
        }
        case 2: {
          const projectservice = await fetchProjectData();
          result = projectservice.map((proj) => ({
            id: proj.projectID,
            name: proj.projectName,
          }));
          break;
        }
        case 3: {
          const mediasourceservice = await fetchMeadiaSourceData();
          result = mediasourceservice.map((source) => ({
            id: source.sourceId,
            name: source.sourceName,
          }));
          break;
        }
        case 4: {
          const subMediaSources = await fetchAllSubMediaSource();
          result = subMediaSources.map((source) => ({
            id: source.id,
            name: source.name,
            isActive: source.isActive,
          }));
          break;
        }
        default: {
          result = [];
          break;
        }
      }
      return result;
    } catch (error) {
      console.error(
        `Error fetching data for assignment type ${typeId}:`,
        error
      );
      toast.error(`Failed to load ${typeId} data`);
      return [];
    }
  };

  // Fetch initial data
  useEffect(() => {
    const getData = async () => {
      setIsLoading(true);
      try {
        const menuID = location.state?.menuID;
        const previousMenuID = localStorage.getItem("activeMenuID");
        if (menuID !== previousMenuID || menuID === null) {
          localStorage.setItem("activeMenuID", menuID);
        } else {
          localStorage.removeItem("activeMenuID");
        }

        const groups = await fetchUserGroupData();
        setUserGroups(groups);

        const usersData = (await fetchUserData()).filter(
          (user) => user.userTypeId == 2
        );
        setUsers(usersData);

        const options = usersData.map((user) => ({
          value: user.userId,
          label: `${user.userName} (${user.email})`,
          userData: user,
        }));
        setUserOptions(options);

        setIsLoadingUserTypes(true);
        const types = await fetchAllUserType();
        if (types && types.length > 0) {
          setUserTypes(types);
          if (!editUserData) {
            setFormData((prev) => ({
              ...prev,
              userTypeId: types[0].typeId.toString(),
            }));
          }
        }

        if (editUserData) {
          populateFormWithEditData(editUserData);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
        toast.error("Failed to load initial data");
      } finally {
        setIsLoading(false);
        setIsLoadingUserTypes(false);
      }
    };

    getData();
  }, [editUserData]);

  // Handle selectedRows
  useEffect(() => {
    if (selectedRows && selectedRows.length > 0) {
      console.log("Selected rows:", selectedRows);
    }
  }, [selectedRows]);

  // Validation functions
  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validateMobile = (mobile: string): boolean => {
    const mobileRegex = /^\d+$/;
    return mobileRegex.test(mobile);
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value, type } = e.target;

    if (type === "checkbox") {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData({
        ...formData,
        [name]: checked,
      });
      return;
    }

    if (value.startsWith(" ")) {
      return;
    }

    if ((name === "mobile" || name === "alternativePhone") && value !== "") {
      if (!/^\d*$/.test(value)) {
        return;
      }
    }

    setFormData({
      ...formData,
      [name]: value,
    });

    if (errors[name as keyof ValidationErrors]) {
      setErrors({
        ...errors,
        [name]: undefined,
      });
    }
  };

  const handleTabClick = (tabId: string) => {
    setActiveTab(tabId);
  };

  const handleSingleSelectChange = (selectedOption: SelectOption | null) => {
    setFormData({
      ...formData,
      reportTo: selectedOption?.userData || null,
    });
  };

  const handleDynamicAssignmentChange = (
    typeId: number,
    newValue: MultiValue<GenericAssignmentOption>,
    actionMeta: ActionMeta<GenericAssignmentOption>
  ) => {
    const selectedOptions = Array.isArray(newValue) ? newValue : [newValue];

    setDynamicAssignmentStates((prevStates) => ({
      ...prevStates,
      [typeId]: {
        ...prevStates[typeId],
        selected: selectedOptions,
        isDropdownChanged: true,
      },
    }));
  };

  const validateForm = (): boolean => {
    const newErrors: ValidationErrors = {};
    let isValid = true;

    if (!formData.userName.trim()) {
      newErrors.userName = "User name is required";
      isValid = false;
    }

    if (!formData.mobile.trim()) {
      newErrors.mobile = "Mobile number is required";
      isValid = false;
    } else if (!validateMobile(formData.mobile)) {
      newErrors.mobile = "Mobile number should contain only digits";
      isValid = false;
    }

    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
      isValid = false;
    } else if (!validateEmail(formData.email)) {
      newErrors.email = "Invalid email format";
      isValid = false;
    }

    if (
      formData.alternativePhone &&
      !validateMobile(formData.alternativePhone)
    ) {
      newErrors.alternativePhone =
        "Alternative phone should contain only digits";
      isValid = false;
    }

    if (!editUserData && !formData.password) {
      newErrors.password = "Password is required";
      isValid = false;
    }

    if (!formData.groupId) {
      newErrors.groupId = "Group selection is required";
      isValid = false;
    }

    if (!formData.userTypeId) {
      newErrors.userTypeId = "User Type selection is required";
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    const userId = localStorage.getItem("UserId");
    const tenantId = localStorage.getItem("tenantId");

    const reportToIds = formData.reportTo
      ? formData.reportTo.userId.toString()
      : "";

    const selectedGroup =
      userGroups?.find(
        (group) => group.groupId?.toString() === formData.groupId
      ) || null;

    const groupName = selectedGroup ? selectedGroup.groupName : "Unknown";

    const body = {
      userId: editUserData ? parseInt(formData.userId) : 0,
      userName: formData.userName,
      mobile: formData.mobile,
      email: formData.email,
      alternativePhone: formData.alternativePhone,
      reportTo: parseInt(reportToIds) || 0,
      passwordHash: formData.password || "default@123",
      groupId: parseInt(formData.groupId),
      isFallbackUser: formData.isFallbackUser,
      isActive: formData.isActive,
      userStatusId: 1,
      userTypeId: formData.userTypeId ? parseInt(formData.userTypeId) : 0,
      modifiedBy: userId ? parseInt(userId) : 0,
    };

    try {
      const result = await updateUser(body);
      if (result) {
        onClose();
        if (onUserDataUpdated) {
          onUserDataUpdated();
        }
        setTimeout(() => {
          navigate("/userView", { state: { userDataUpdated: true } });
        }, 1000);
        toast.success("User updated successfully");
      } else {
        toast.error("User operation failed");
      }
    } catch (error) {
      console.error("API Error:", error);
      toast.error("Error occurred during user operation");
    }
  };

  const handleAssignmentSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const userId = userData?.userId || 0;
    const tenantId = localStorage.getItem("tenantId");
    const createdBy = localStorage.getItem("UserId");

    try {
      setIsSubmittingAssignment(true);
      for (const typeId of dynamicAssignmentTypeIds) {
        const state = dynamicAssignmentStates[typeId];

        if (!state.isDropdownChanged) {
          continue;
        }

        const currentSelected = state.selected.map((item) => item.value);

        const unselectedItems = state.previousSelected.filter(
          (itemId) => !currentSelected.includes(itemId)
        );

        if (unselectedItems.length > 0) {
          const assignmentRequest: UserAssignmentRequest = {
            userId,
            assignmentTypeId: typeId,
            listOfAssignments: unselectedItems.map((itemId) => ({
              assignmentObjectId: itemId,
            })),
            isAssigned: false,
            tenantId: tenantId ? parseInt(tenantId) : 0,
            createdBy: createdBy ? parseInt(createdBy) : 0,
          };

          await UpdateUserTargets(assignmentRequest);
        }

        if (currentSelected.length > 0) {
          const assignmentRequest: UserAssignmentRequest = {
            userId,
            assignmentTypeId: typeId,
            listOfAssignments: currentSelected.map((itemId) => ({
              assignmentObjectId: itemId,
            })),
            isAssigned: true,
            tenantId: tenantId ? parseInt(tenantId) : 0,
            createdBy: createdBy ? parseInt(createdBy) : 0,
          };

          await UpdateUserTargets(assignmentRequest);
        }

        setDynamicAssignmentStates((prevStates) => ({
          ...prevStates,
          [typeId]: {
            ...prevStates[typeId],
            previousSelected: currentSelected,
            isDropdownChanged: false,
          },
        }));
      }

      toast.success("Assigned successfully");
      onClose();
      if (onUserDataUpdated) {
        onUserDataUpdated();
      }
    } catch (error) {
      console.error("Error assigning projects:", error);
      toast.error("An error occurred while assigning projects");
    } finally {
      setIsSubmittingAssignment(false);
    }
  };

  const requiredField = <span className="text-danger ms-1">*</span>;

  const handleClose = () => {
    setFormData({
      userId: "",
      userName: "",
      mobile: "",
      email: "",
      alternativePhone: "",
      reportTo: null,
      password: "",
      groupId: "",
      isFallbackUser: false,
      isActive: true,
      userTypeId: "",
    });
    setErrors({});
    onClose();
  };

  return (
    <>
      <Toaster />
      <div
        className={`modal-backdrop-custom ${isOpen ? "open" : "closed"}`}
        onClick={handleClose}
      ></div>

      <div className={`modal-custom ${isOpen ? "open" : "closed"}`}>
        <div className="modal-content">
          <div className="modal-header-custom">
            <h5 className="modal-title-custom">
              {editUserData ? "Edit User" : "Create User"}
            </h5>
            <button
              type="button"
              className="btn-close-custom"
              onClick={handleClose}
            >
              <span className="icon-close"></span>
            </button>
          </div>
          <ul
            className="nav nav-tabs pop-up-tabHeader"
            id="myTab"
            role="tablist"
            style={{ borderBottom: "1px solid rgb(206 212 218)" }}
          >
            <li className="nav-item" role="presentation">
              <button
                className={`nav-link card-title tab-btn pop-up-tabHeaderbtn ${
                  activeTab === "tab1" ? "active" : ""
                }`}
                id="tab1-tab"
                onClick={() => handleTabClick("tab1")}
                type="button"
                role="tab"
                aria-controls="tab1-tab-pane"
                aria-selected={activeTab === "tab1"}
              >
                User Details
              </button>
            </li>
            <li className="nav-item" role="presentation">
              <button
                className={`nav-link card-title tab-btn pop-up-tabHeaderbtn ${
                  activeTab === "tab3" ? "active" : ""
                }`}
                id="tab3-tab"
                onClick={() => handleTabClick("tab3")}
                type="button"
                role="tab"
                aria-controls="tab3-tab-pane"
                aria-selected={activeTab === "tab3"}
              >
                Assigned Information
              </button>
            </li>
          </ul>
          <div className="modal-body-custom">
            <div className="card pop-up-card">
              <div className="tab-content card-body p-0" id="myTabContent">
                <div
                  className={`tab-pane fade ${
                    activeTab === "tab1" ? "show active" : ""
                  }`}
                  id="tab1-tab-pane"
                  role="tabpanel"
                  aria-labelledby="tab1-tab"
                  tabIndex={0}
                >
                  <div className="modal-body-content">
                    <form onSubmit={handleSubmit}>
                      <div className="row">
                        <div className="col-12 col-lg-12 mb-3 input-group-custom">
                          <label className="form-label" htmlFor="userName">
                            User Name{requiredField}
                          </label>
                          <div className="input-with-icon">
                            <span className="input-icon icon-user"></span>
                            <input
                              name="userName"
                              id="userName"
                              placeholder="Enter User Name"
                              value={formData.userName}
                              onChange={handleChange}
                              required
                              className={`form-control-custom ${
                                errors.userName ? "is-invalid" : ""
                              }`}
                            />
                            {errors.userName && (
                              <div className="invalid-feedback-custom">
                                {errors.userName}
                              </div>
                            )}
                          </div>
                        </div>

                        <div className="col-12 col-lg-6 mb-3 input-group-custom">
                          <label className="form-label" htmlFor="mobile">
                            Mobile No.{requiredField}
                          </label>
                          <div className="input-with-icon">
                            <span className="input-icon icon-phone"></span>
                            <input
                              name="mobile"
                              id="mobile"
                              placeholder="Enter Mobile No."
                              value={formData.mobile}
                              onChange={handleChange}
                              required
                              className={`form-control-custom ${
                                errors.mobile ? "is-invalid" : ""
                              }`}
                            />
                            {errors.mobile && (
                              <div className="invalid-feedback-custom">
                                {errors.mobile}
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="col-12 col-lg-6 mb-3 input-group-custom">
                          <label
                            className="form-label"
                            htmlFor="alternativePhone"
                          >
                            Alternative Phone
                          </label>
                          <div className="input-with-icon">
                            <span className="input-icon icon-phone"></span>
                            <input
                              name="alternativePhone"
                              id="alternativePhone"
                              placeholder="Enter Alternative Phone"
                              value={formData.alternativePhone}
                              onChange={handleChange}
                              className={`form-control-custom ${
                                errors.alternativePhone ? "is-invalid" : ""
                              }`}
                            />
                            {errors.alternativePhone && (
                              <div className="invalid-feedback-custom">
                                {errors.alternativePhone}
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="col-12 col-lg-12 mb-3 input-group-custom">
                          <label className="form-label" htmlFor="email">
                            Email{requiredField}
                          </label>
                          <div className="input-with-icon">
                            <span className="input-icon icon-email"></span>
                            <input
                              name="email"
                              id="email"
                              placeholder="Enter Email"
                              value={formData.email}
                              onChange={handleChange}
                              required
                              className={`form-control-custom ${
                                errors.email ? "is-invalid" : ""
                              }`}
                            />
                            {errors.email && (
                              <div className="invalid-feedback-custom">
                                {errors.email}
                              </div>
                            )}
                          </div>
                        </div>

                        <div className="col-12 col-lg-12 mb-3 input-group-custom">
                          <label className="form-label">Reports To</label>
                          <Select
                            options={userOptions}
                            value={
                              formData.reportTo
                                ? {
                                    value: formData.reportTo.userId,
                                    label: `${formData.reportTo.userName} (${formData.reportTo.email})`,
                                    userData: formData.reportTo,
                                  }
                                : null
                            }
                            onChange={handleSingleSelectChange}
                            className="basic-single-select"
                            classNamePrefix="select"
                            placeholder="Select manager"
                            isLoading={isLoading}
                            isClearable
                          />
                        </div>

                        {/* <div className="col-12 col-lg-6 mb-3 input-group-custom">
                          <label className="form-label" htmlFor="password">
                            Password{!editUserData ? requiredField : ""}
                          </label>
                          <div className="input-with-icon">
                            <span className="input-icon icon-password"></span>
                            <input
                              name="password"
                              id="password"
                              type="password"
                              placeholder={
                                editUserData
                                  ? "Leave blank to keep current password"
                                  : "Enter password"
                              }
                              value={formData.password}
                              onChange={handleChange}
                              required={!editUserData}
                              className={`form-control-custom ${
                                errors.password ? "is-invalid" : ""
                              }`}
                            />
                            {errors.password && (
                              <div className="invalid-feedback-custom">
                                {errors.password}
                              </div>
                            )}
                          </div>
                        </div> */}

                        <div className="col-12 col-lg-12 mb-3 input-group-custom">
                          <label className="form-label">
                            Group{requiredField}
                          </label>
                          <select
                            name="groupId"
                            value={formData.groupId}
                            onChange={handleChange}
                            required
                            className={`form-select form-control-lg ${
                              errors.groupId ? "is-invalid" : ""
                            }`}
                          >
                            <option value="">Select Group</option>
                            {userGroups.map((group) => (
                              <option key={group.groupId} value={group.groupId}>
                                {group.groupName}
                              </option>
                            ))}
                          </select>
                          {errors.groupId && (
                            <div className="invalid-feedback text-danger">
                              {errors.groupId}
                            </div>
                          )}
                        </div>

                        <div className="col-12 col-lg-12 mb-3 input-group-custom">
                          <label className="form-label">
                            User Type{requiredField}
                          </label>
                          <select
                            name="userTypeId"
                            value={formData.userTypeId}
                            onChange={handleChange}
                            required
                            className={`form-select form-control-lg ${
                              errors.userTypeId ? "is-invalid" : ""
                            }`}
                            disabled={
                              userTypes.length === 0 || isLoadingUserTypes
                            }
                          >
                            {isLoadingUserTypes ? (
                              <option value="">Loading user types...</option>
                            ) : userTypes.length > 0 ? (
                              userTypes.map((type) => (
                                <option key={type.typeId} value={type.typeId}>
                                  {type.typeName}
                                </option>
                              ))
                            ) : (
                              <option value="">No user types available</option>
                            )}
                          </select>
                          {errors.userTypeId && (
                            <div className="invalid-feedback text-danger">
                              {errors.userTypeId}
                            </div>
                          )}
                        </div>

                        <div className="col-12 col-lg-12 mb-3 mt-3">
                          <div className="row">
                            <div className="col-12 col-lg-6">
                              <label className="form-check">
                                Is Fallback User
                                <input
                                  type="checkbox"
                                  name="isFallbackUser"
                                  checked={formData.isFallbackUser}
                                  onChange={handleChange}
                                  className="form-check-input"
                                />
                              </label>
                            </div>
                            <div className="col-12 col-lg-6">
                              <label className="form-check">
                                Is Active
                                <input
                                  type="checkbox"
                                  name="isActive"
                                  checked={formData.isActive}
                                  onChange={handleChange}
                                  className="form-check-input"
                                />
                              </label>
                            </div>
                          </div>
                        </div>
                      </div>
                    </form>
                  </div>
                </div>

                <div
                  className={`tab-pane fade ${
                    activeTab === "tab3" ? "show active" : ""
                  }`}
                  id="tab3-tab-pane"
                  role="tabpanel"
                  aria-labelledby="tab3-tab"
                  tabIndex={0}
                >
                  <form onSubmit={handleAssignmentSubmit}>
                    <div className="row">
                      {dynamicAssignmentTypeIds.length > 0 ? (
                        dynamicAssignmentTypeIds.map((typeId) => (
                          <div className="col-12 col-lg-12 mb-3" key={typeId}>
                            <label className="form-label">
                              Assigned{" "}
                              {dynamicAssignmentStates[typeId]?.typeName ||
                                "Unknown"}
                            </label>
                            <Select
                              isMulti
                              options={
                                dynamicAssignmentStates[typeId]?.options || []
                              }
                              value={
                                dynamicAssignmentStates[typeId]?.selected || []
                              }
                              onChange={(
                                newValue: MultiValue<GenericAssignmentOption>,
                                actionMeta: ActionMeta<GenericAssignmentOption>
                              ) =>
                                handleDynamicAssignmentChange(
                                  typeId,
                                  newValue,
                                  actionMeta
                                )
                              }
                              className="basic-multi-select"
                              classNamePrefix="select"
                              placeholder={`Select ${
                                dynamicAssignmentStates[typeId]?.typeName ||
                                "items"
                              } to assign`}
                              isLoading={
                                dynamicAssignmentStates[typeId]?.isLoading ||
                                false
                              }
                            />
                            <div className="form-text">
                              Select multiple{" "}
                              {dynamicAssignmentStates[typeId]?.typeName ||
                                "items"}{" "}
                              to assign to this user
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="col-12 col-lg-12 mb-3">
                          <p>No assignment types available.</p>
                        </div>
                      )}
                    </div>
                  </form>
                </div>
              </div>
            </div>

            <div className="modal-footer-custom">
              <div>
                {activeTab !== "tab3" ? (
                  <>
                    <button
                      type="button"
                      onClick={handleClose}
                      className="btn-custom btn-cancel"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="btn-custom btn-primary-custom"
                      onClick={handleSubmit}
                    >
                      Update User
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      type="button"
                      onClick={handleClose}
                      className="btn-custom btn-cancel"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="btn-custom btn-primary-custom"
                      onClick={handleAssignmentSubmit}
                      disabled={isSubmittingAssignment}
                    >
                      {isSubmittingAssignment ? "Saving..." : "Save Assigned"}
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default EditUserViewDeom;
